import { FC } from 'react';
import { BadgeProps, ButtonProps, ButtonSizes, ButtonStyles, Icons } from '@silverfort/sf-components';
import { PolicyInsight, PolicyInsightsConfig } from '../../../types/policies';
import InsightItem from './InsightItem';
import { LockedFeatureDisplayConfig } from '../../../components/common/LockedFeatures';
import { getPolicyTemplatesLockedFeatureTooltipConfig } from '../../../utils/policies/lockedFeatures';
import { usePricingModulesState } from '../../../context/contexts/PricingModuleContext';

interface Props {
    insights: PolicyInsight[];
    insightsConfig: PolicyInsightsConfig;
    canCreatePolicy?: boolean;
    onCreatePolicyClick: (insightId: PolicyInsight['id']) => void;
    onViewPolicyClick: (insightId: PolicyInsight['id']) => void;
    onResolveInsightClick: (insightId: PolicyInsight['id']) => void;
    onReopenInsightClick: (insightId: PolicyInsight['id']) => void;
}

const InsightsList: FC<Props> = ({
    insights,
    insightsConfig,
    canCreatePolicy = true,
    onCreatePolicyClick,
    onViewPolicyClick,
    onReopenInsightClick,
    onResolveInsightClick
}) => {
    const { categories, hiddenRiskIndicators } = insightsConfig;
    const { pricingModulesState } = usePricingModulesState();

    const getButtonProps = (insight: PolicyInsight): ButtonProps => {
        if (insight.resolved) {
            return {
                text: 'View policy',
                disabled: insight.policyId === undefined,
                iconRight: Icons.ArrowForward,
                buttonStyle: ButtonStyles.Primary,
                buttonSize: ButtonSizes.Small,
                onClick: () => onViewPolicyClick(insight.id)
            };
        }

        return {
            text: 'Create policy',
            disabled: !canCreatePolicy,
            iconRight: Icons.ArrowForward,
            buttonStyle: ButtonStyles.Primary,
            buttonSize: ButtonSizes.Small,
            onClick: () => onCreatePolicyClick(insight.id)
        };
    };

    const getButtonLockFeatureDisplayConfig = (insight: PolicyInsight): LockedFeatureDisplayConfig => {
        if (insight.resolved) {
            return { enabled: true };
        }

        return getPolicyTemplatesLockedFeatureTooltipConfig({
            hiddenRiskIndicators,
            pricingModulesState,
            template: insight.template
        });
    };

    const getCategoryBadgeProps = (category: PolicyInsight['category']): BadgeProps | undefined => {
        const categoryConfig = categories[category];

        if (!categoryConfig) {
            return undefined;
        }

        return {
            text: categoryConfig.name,
            icon: categoryConfig.iconName in Icons ? (categoryConfig.iconName as keyof typeof Icons) : undefined
        };
    };

    const getMenuItems = (insight: PolicyInsight) => {
        if (insight.resolved) {
            return [
                {
                    id: 'reopen',
                    label: 'Move to open',
                    onClick: () => onReopenInsightClick(insight.id)
                }
            ];
        }

        return [
            {
                id: 'resolve',
                label: 'Move to completed',
                onClick: () => onResolveInsightClick(insight.id)
            }
        ];
    };

    return (
        <div className="p-4 flex flex-col gap-4 h-full overflow-auto">
            {insights.map((insight) => (
                <InsightItem
                    key={insight.id}
                    title={insight.name}
                    description={insight.resolved ? undefined : insight.description}
                    badgeProps={getCategoryBadgeProps(insight.category)}
                    buttonProps={getButtonProps(insight)}
                    buttonLockFeatureDisplayConfig={getButtonLockFeatureDisplayConfig(insight)}
                    menuItems={getMenuItems(insight)}
                />
            ))}
        </div>
    );
};

export default InsightsList;
